import { decodeNodeFromYjs, syncEncodedNodeToYMap } from '#react/app/collab/node-codec'
import type { EditorStore } from '#react/app/editor/store'
import * as Y from 'yjs'

type YNodes = Y.Map<Y.Map<unknown>>
type YImages = Y.Map<Uint8Array>

type GraphBindingOptions = {
  store: EditorStore
  getYdoc: () => Y.Doc | null
  getYnodes: () => YNodes | null
  getSuppressGraphSync: () => boolean
  /** False while alone in the room — presence only, no graph↔Yjs traffic. */
  getDocumentSyncEnabled: () => boolean
  setSuppressYjsEvents: (value: boolean) => void
  syncNodeToYjs: (nodeId: string) => void
  /** One transaction for the whole document — required on graph:replaced. */
  syncAllNodesToYjs: () => void
}

type YjsObserverOptions = {
  store: EditorStore
  ynodes: Y.Map<Y.Map<unknown>>
  yimages: Y.Map<Uint8Array>
  getSuppressYjsEvents: () => boolean
  setSuppressGraphSync: (value: boolean) => void
  applyYjsToGraph: (events: Y.YEvent<Y.Map<unknown>>[]) => void
}

type YjsGraphSyncOptions = {
  getStore: () => EditorStore
  getYdoc: () => Y.Doc | null
  getYnodes: () => YNodes | null
  getYimages: () => YImages | null
  setSuppressYjsEvents: (value: boolean) => void
}

function logCollabSyncError(context: string, error: unknown) {
  console.error(`[Collab] ${context}:`, error)
}

export function bindCollabGraphEvents({
  store,
  getYdoc,
  getYnodes,
  getSuppressGraphSync,
  getDocumentSyncEnabled,
  setSuppressYjsEvents,
  syncNodeToYjs,
  syncAllNodesToYjs
}: GraphBindingOptions) {
  function onGraphMutation(nodeId: string) {
    if (
      getDocumentSyncEnabled() &&
      !getSuppressGraphSync() &&
      getYdoc() &&
      getYnodes()
    ) {
      syncNodeToYjs(nodeId)
    }
  }

  const pendingPreview = new Set<string>()
  let previewTimer: number | null = null
  let replaceSyncTimer: number | null = null
  function onPreviewMutation(nodeId: string) {
    pendingPreview.add(nodeId)
    if (previewTimer !== null) return
    previewTimer = window.setTimeout(() => {
      previewTimer = null
      for (const id of pendingPreview) onGraphMutation(id)
      pendingPreview.clear()
    }, 32)
  }

  let unbindPreview = store.graph.onNodeEvents({
    previewUpdated: (id) => onPreviewMutation(id)
  })

  const unbinds = [
    store.onEditorEvent('graph:replaced', () => {
      unbindPreview()
      unbindPreview = store.graph.onNodeEvents({
        previewUpdated: (id) => onPreviewMutation(id)
      })
      // Alone in the room: keep presence only — FIG open must not pay for a full seed.
      // Never sync node-by-node: each call opens its own Y.Doc transaction.
      if (
        !getDocumentSyncEnabled() ||
        getSuppressGraphSync() ||
        !getYdoc() ||
        !getYnodes()
      ) {
        return
      }
      if (replaceSyncTimer !== null) window.clearTimeout(replaceSyncTimer)
      replaceSyncTimer = window.setTimeout(() => {
        replaceSyncTimer = null
        if (
          !getDocumentSyncEnabled() ||
          getSuppressGraphSync() ||
          !getYdoc() ||
          !getYnodes()
        ) {
          return
        }
        const t0 = performance.now()
        syncAllNodesToYjs()
        console.info(
          `[fig] ui.graph:replaced.yjsSyncAll: ${(performance.now() - t0).toFixed(1)} ms`
        )
      }, 0)
    }),
    store.onEditorEvent('node:updated', (id) => onGraphMutation(id)),
    store.onEditorEvent('node:created', (node) => {
      onGraphMutation(node.id)
      if (node.parentId) onGraphMutation(node.parentId)
    }),
    store.onEditorEvent('node:reparented', (nodeId, oldParentId, newParentId) => {
      onGraphMutation(nodeId)
      if (oldParentId) onGraphMutation(oldParentId)
      onGraphMutation(newParentId)
    }),
    store.onEditorEvent('node:reordered', (nodeId, parentId) => {
      onGraphMutation(nodeId)
      onGraphMutation(parentId)
    }),
    store.onEditorEvent('node:deleted', (id) => {
      const ydoc = getYdoc()
      const ynodes = getYnodes()
      if (getDocumentSyncEnabled() && !getSuppressGraphSync() && ydoc && ynodes) {
        const parentId = ynodes.get(id)?.get('parentId')
        setSuppressYjsEvents(true)
        try {
          ydoc.transact(() => {
            ynodes.delete(id)
          })
        } catch (error) {
          logCollabSyncError('Failed to delete synced node', error)
        } finally {
          setSuppressYjsEvents(false)
        }
        if (typeof parentId === 'string') onGraphMutation(parentId)
        else onGraphMutation(store.graph.rootId)
      }
    })
  ]
  return () => {
    if (previewTimer !== null) window.clearTimeout(previewTimer)
    if (replaceSyncTimer !== null) window.clearTimeout(replaceSyncTimer)
    pendingPreview.clear()
    unbindPreview()
    for (const unbind of unbinds) unbind()
  }
}

export function registerYjsObservers({
  store,
  ynodes,
  yimages,
  getSuppressYjsEvents,
  setSuppressGraphSync,
  applyYjsToGraph
}: YjsObserverOptions) {
  ynodes.observeDeep((events) => {
    if (getSuppressYjsEvents()) return
    setSuppressGraphSync(true)
    try {
      applyYjsToGraph(events)
      store.requestRender()
    } catch (error) {
      logCollabSyncError('Failed to apply remote graph changes', error)
    } finally {
      setSuppressGraphSync(false)
    }
  })

  yimages.observe((event) => {
    if (getSuppressYjsEvents()) return
    try {
      for (const [key, change] of event.changes.keys) {
        if (change.action === 'add' || change.action === 'update') {
          const data = yimages.get(key)
          if (data) store.graph.images.set(key, new Uint8Array(data))
        } else {
          store.graph.images.delete(key)
        }
      }
      store.requestRender()
    } catch (error) {
      logCollabSyncError('Failed to apply remote image changes', error)
    }
  })
}

export function createYjsGraphSync({
  getStore,
  getYdoc,
  getYnodes,
  getYimages,
  setSuppressYjsEvents
}: YjsGraphSyncOptions) {
  function syncNodeToYjs(nodeId: string) {
    const store = getStore()
    const ydoc = getYdoc()
    const ynodes = getYnodes()
    if (!ydoc || !ynodes) return
    const node = store.graph.getNode(nodeId)
    if (!node) return

    const localYimages = getYimages()
    setSuppressYjsEvents(true)
    try {
      ydoc.transact(() => {
        let ynode = ynodes.get(nodeId)
        if (!ynode) {
          ynode = new Y.Map()
          ynodes.set(nodeId, ynode)
        }
        syncEncodedNodeToYMap(node, ynode)

        if (localYimages) {
          for (const fill of node.fills) {
            if (fill.imageHash && !localYimages.has(fill.imageHash)) {
              const data = store.graph.images.get(fill.imageHash)
              if (data) localYimages.set(fill.imageHash, data)
            }
          }
        }
      })
    } catch (error) {
      logCollabSyncError(`Failed to sync node ${nodeId}`, error)
    } finally {
      setSuppressYjsEvents(false)
    }
  }

  function syncMissingNodesToYjs() {
    const store = getStore()
    const ynodes = getYnodes()
    if (!ynodes) return
    for (const node of store.graph.getAllNodes()) {
      if (!ynodes.has(node.id)) syncNodeToYjs(node.id)
    }
  }

  function syncAllNodesToYjs() {
    const store = getStore()
    const ydoc = getYdoc()
    const ynodes = getYnodes()
    if (!ydoc || !ynodes) return
    const localYimages = getYimages()
    setSuppressYjsEvents(true)
    try {
      ydoc.transact(() => {
        const liveIds = new Set<string>()
        for (const node of store.graph.getAllNodes()) {
          liveIds.add(node.id)
          let ynode = ynodes.get(node.id)
          if (!ynode) {
            ynode = new Y.Map()
            ynodes.set(node.id, ynode)
          }
          syncEncodedNodeToYMap(node, ynode)
        }
        // Document replace must drop peers' stale ids from the previous graph.
        for (const key of [...ynodes.keys()]) {
          if (!liveIds.has(key)) ynodes.delete(key)
        }
      })
      if (localYimages) {
        ydoc.transact(() => {
          for (const [hash, data] of store.graph.images) {
            if (!localYimages.has(hash)) {
              localYimages.set(hash, data)
            }
          }
        })
      }
    } catch (error) {
      logCollabSyncError('Failed to sync document', error)
    } finally {
      setSuppressYjsEvents(false)
    }
  }

  function applyYjsToGraph(events: Y.YEvent<Y.Map<unknown>>[]) {
    const store = getStore()
    const ynodes = getYnodes()
    if (!ynodes) return
    for (const event of events) {
      if (event.target === ynodes) {
        for (const [key, change] of event.changes.keys) {
          if (change.action === 'add') {
            const ynode = ynodes.get(key)
            if (ynode) applyYnodeToGraph(key, ynode)
          } else if (change.action === 'delete') {
            store.graph.deleteNode(key)
          }
        }
      } else if (event.target.parent === ynodes) {
        const nodeId = findNodeIdForYMap(event.target)
        if (nodeId) {
          const ynode = ynodes.get(nodeId)
          if (ynode) applyYnodeToGraph(nodeId, ynode)
        }
      }
    }
  }

  function findNodeIdForYMap(ymap: Y.Map<unknown>): string | null {
    const ynodes = getYnodes()
    if (!ynodes) return null
    for (const [key, value] of ynodes.entries()) {
      if (value === ymap) return key
    }
    return null
  }

  function applyYnodeToGraph(nodeId: string, ynode: Y.Map<unknown>) {
    const store = getStore()
    const existing = store.graph.getNode(nodeId)
    const props = decodeNodeFromYjs(ynode)
    const parentId = typeof props.parentId === 'string' ? props.parentId : null
    const childIds = mergeYjsChildIds(existing?.childIds ?? [], props.childIds, (id) => {
      return store.graph.getNode(id)?.parentId === nodeId
    })

    if (existing) {
      store.graph.updateNode(nodeId, childIds ? { ...props, childIds } : props)
      if (parentId === null) store.graph.rootId = nodeId
      ensureCurrentPageExists(store)
      return
    }

    const type = props.type
    if (!type) return
    // Parent childIds may arrive before or after the child node.
    store.graph.createNodeWithId(nodeId, type, parentId, props)
    if (parentId === null) store.graph.rootId = nodeId
    ensureCurrentPageExists(store)
  }

  function ensureCurrentPageExists(store: EditorStore) {
    const pages = store.graph.getPages()
    if (pages.some((page) => page.id === store.state.currentPageId)) return
    if (pages.length === 0) return
    void store.switchPage(pages[0].id)
  }

  return { syncNodeToYjs, syncAllNodesToYjs, syncMissingNodesToYjs, applyYjsToGraph }
}

export function mergeYjsChildIds(
  existingChildIds: readonly string[],
  incomingChildIds: unknown,
  childStillAttached: (id: string) => boolean
): string[] | undefined {
  if (!Array.isArray(incomingChildIds)) return undefined
  const incoming = incomingChildIds.filter((id): id is string => typeof id === 'string')
  const extras = existingChildIds.filter((id) => !incoming.includes(id) && childStillAttached(id))
  return extras.length === 0 ? incoming : [...incoming, ...extras]
}
